import React, { useState } from 'react';
import styled from 'styled-components';
import { GrowthNav } from './GrowthNav';
import { useGrowthBriefs, useGrowthBriefUpdate } from '../../hooks/useGrowthBriefs';

const Page = styled.div`
  background: var(--background);
  min-height: 100%;
`;

const Card = styled.div`
  padding: 1.25rem;
  border-radius: 14px;
  background: var(--surface);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--outline);
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 320px;
  padding: 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
`;

const Select = styled.select`
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
  margin-right: 0.5rem;
`;

const Btn = styled.button<{ $primary?: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid var(--outline);
  background: ${({ $primary }) => ($primary ? 'var(--primary)' : 'var(--surface)')};
  color: ${({ $primary }) => ($primary ? 'var(--onPrimary)' : 'var(--onSurface)')};
  cursor: pointer;
  margin-right: 0.5rem;
  &:disabled {
    opacity: 0.6;
  }
`;

export default function GrowthBriefsPage() {
  const { data: briefs = [], isLoading } = useGrowthBriefs();
  const updateBrief = useGrowthBriefUpdate();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  const handleEdit = (id: string, content_md: string) => {
    setEditingId(id);
    setEditContent(content_md);
  };

  const handleSave = () => {
    if (!editingId) return;
    updateBrief.mutate(
      { id: editingId, updates: { content_md: editContent } },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleStatusChange = (id: string, status: 'draft' | 'approved' | 'sent') => {
    updateBrief.mutate({ id, updates: { status } });
  };

  if (isLoading) return <Page><GrowthNav /><p>Loading briefs…</p></Page>;

  return (
    <Page>
      <GrowthNav />
      <SectionTitle style={{ marginTop: 0 }}>Weekly briefs</SectionTitle>
      {briefs.map((b) => (
        <Card key={b.id}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <SectionTitle style={{ margin: 0 }}>Week of {b.week_of}</SectionTitle>
            <Select
              value={b.status}
              onChange={(e) => handleStatusChange(b.id, e.target.value as 'draft' | 'approved' | 'sent')}
            >
              <option value="draft">Draft</option>
              <option value="approved">Approved</option>
              <option value="sent">Sent</option>
            </Select>
            {editingId === b.id ? (
              <>
                <Btn $primary onClick={handleSave} disabled={updateBrief.isPending}>Save</Btn>
                <Btn onClick={() => setEditingId(null)}>Cancel</Btn>
              </>
            ) : (
              <Btn onClick={() => handleEdit(b.id, b.content_md)}>Edit</Btn>
            )}
          </div>
          {editingId === b.id ? (
            <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} />
          ) : (
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, fontSize: '0.9rem' }}>
              {b.content_md}
            </pre>
          )}
        </Card>
      ))}
    </Page>
  );
}
